import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import { DialogPresets } from '@principle-theorem/ng-shared';
import { Brand } from '@principle-theorem/principle-core';
import { IPatient } from '@principle-theorem/principle-core/interfaces';
import {
  snapshotDefined,
  snapshot,
  WithRef,
  Firestore,
  addDoc,
} from '@principle-theorem/shared';
import { AddPatientComponent } from '../add-patient/add-patient.component';
import {
  UpdatePatientComponent,
  IUpdatePatientData,
} from '../update-patient/update-patient.component';
import { ReplaySubject } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'pr-manage-patient-button',
  imports: [CommonModule, MatIconModule, RouterModule],
  templateUrl: './manage-patient-button.component.html',
  styleUrl: './manage-patient-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex' },
})
export class ManagePatientButtonComponent {
  private _organisation = inject(OrganisationService);
  private _dialog = inject(MatDialog);
  private _snackBar = inject(MatSnackBar);
  patient$ = new ReplaySubject<WithRef<IPatient> | undefined>(1);

  @Output() patientChange = new EventEmitter<WithRef<IPatient>>();

  @Input()
  set patient(patient: WithRef<IPatient> | undefined) {
    this.patient$.next(patient);
  }

  async addPatient(): Promise<void> {
    const data = await this._dialog
      .open<AddPatientComponent, undefined, IPatient>(
        AddPatientComponent,
        DialogPresets.large({ autoFocus: true })
      )
      .afterClosed()
      .toPromise();

    if (!data) {
      return;
    }
    const brand = await snapshotDefined(this._organisation.brand$);
    const collection = Brand.patientCol(brand);
    const docRef = await addDoc(collection, data);
    const patient = await Firestore.getDoc(docRef);
    this._snackBar.open(`Added ${patient.name}`);
    this.patientChange.emit(patient);
  }

  async editPatient(): Promise<void> {
    const patient = await snapshot(this.patient$);

    if (!patient) {
      return;
    }

    const result = await this._dialog
      .open<UpdatePatientComponent, IUpdatePatientData, WithRef<IPatient>>(
        UpdatePatientComponent,
        DialogPresets.large({ autoFocus: true, data: { patient } })
      )
      .afterClosed()
      .toPromise();

    if (!result) {
      return;
    }
    this._snackBar.open(`${result.name} Updated`);
    this.patientChange.emit(result);
  }
}
